// Which parts of the app get used (Settings > Usage; docs/areas/usage.md).
// Ranks screens and key actions over 7, 30 or 90 days with the trend against
// the window before, and names what has gone 30 days unused. The numbers are
// counters on our own server; the switch stops recording and Clear forgets.
import { useCallback, useEffect, useState } from 'react'
import { api } from '@/runtime'
import {
  rankUsage,
  unusedFor30,
  daysToFlags,
  type RankedUsage,
  type FeatureUsageReport,
  type UsageWindow,
} from '@/lib/usage'
import { fmtDateShort } from '@/lib/time'
import { Button, Failed, Segmented, cx } from './ui'
import { Skeleton } from './Skeleton'
import { SwitchRow } from './Switch'

const CAP = 12

/** The window's days as a line; the scale is its own busiest day. */
const Spark = ({ values }: { values: number[] }) => {
  const w = 56
  const h = 18
  const max = Math.max(1, ...values)
  const pts = values
    .map(
      (v, i) =>
        `${((i / Math.max(1, values.length - 1)) * w).toFixed(1)},${(h - 1 - (v / max) * (h - 3)).toFixed(1)}`,
    )
    .join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0 text-accent" aria-hidden>
      <polyline
        points={pts}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

export const trendWords = (r: Pick<RankedUsage, 'trend' | 'before'>): string => {
  if (r.trend === null) return 'New'
  if (r.trend === 0) return 'Steady'
  return `${r.trend > 0 ? 'Up' : 'Down'} ${Math.abs(r.trend)}%`
}

const Row = ({ r }: { r: RankedUsage }) => (
  <li className="flex items-center gap-3 px-3.5 py-2.5" data-testid="usage-row">
    <span className="min-w-0 flex-1">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-[15px] truncate">{r.label}</span>
        <span className="text-xs text-muted tabular-nums shrink-0">{r.count}</span>
      </span>
      <span className="block h-1 rounded-full bg-line mt-1.5 overflow-hidden">
        <span
          className="block h-full bg-accent rounded-full"
          style={{ width: `${Math.max(3, r.share * 100)}%` }}
        />
      </span>
      <span className="flex gap-x-3 text-xs text-faint mt-1">
        <span>{trendWords(r)}</span>
        {r.mostly && <span>Mostly {r.mostly}</span>}
        {r.kind === 'action' && <span>Action</span>}
      </span>
    </span>
    <Spark values={r.daily} />
  </li>
)

export const FeatureUsage = () => {
  const [report, setReport] = useState<FeatureUsageReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [days, setDays] = useState<UsageWindow>(30)
  const [all, setAll] = useState(false)
  const [allUnused, setAllUnused] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    api
      .usage()
      .then((r) => {
        setError(null)
        setReport(r)
      })
      .catch((e: Error) => setError(e.message))
  }, [])
  useEffect(() => {
    let stale = false
    api
      .usage()
      .then((r) => {
        if (!stale) setReport(r)
      })
      .catch((e: Error) => {
        if (!stale) setError(e.message)
      })
    return () => {
      stale = true
    }
  }, [])

  if (error && !report) return <Failed what="your usage" reason={error} onRetry={load} />
  if (!report)
    return (
      <div className="flex flex-col gap-2" data-testid="skeleton" aria-busy="true" aria-label="Loading">
        <Skeleton className="h-14" />
        <Skeleton className="h-64" />
      </div>
    )

  const ranked = rankUsage(report, days)
  const shown = all ? ranked : ranked.slice(0, CAP)
  const unused = unusedFor30(report)
  const unusedShown = allUnused ? unused : unused.slice(0, CAP)
  const wait = daysToFlags(report)

  const flip = async (on: boolean) => {
    setBusy(true)
    try {
      const st = await api.usageSwitch(on)
      setReport({ ...report, enabled: st.enabled, since: st.since })
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const clear = async () => {
    setBusy(true)
    try {
      await api.usageClear()
      setConfirming(false)
      load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="feature-usage">
      <p className="text-sm text-muted mb-3">
        Which screens you open and which key actions you use, counted per day on this server and nowhere else.
        Never the content, never an id, and nothing from Wellbeing.
      </p>
      <div className="card px-3.5 mb-4">
        <SwitchRow
          label="Record usage"
          hint={
            report.since
              ? `Counting since ${fmtDateShort(report.since).replace(/^(Today|Yesterday)$/, (w) => w.toLowerCase())}.`
              : 'Nothing counted yet.'
          }
          on={report.enabled}
          disabled={busy}
          onChange={(on) => void flip(on)}
          testId="usage-record"
        />
      </div>
      {error && <p className="text-sm text-muted mb-3">{error}</p>}

      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-[15px] font-semibold">Most used</h3>
        <Segmented
          label="Period"
          value={days}
          options={[
            { value: 7, label: '7 days' },
            { value: 30, label: '30 days' },
            { value: 90, label: '90 days' },
          ]}
          onChange={(v) => {
            setDays(v)
            setAll(false)
          }}
        />
      </div>
      {ranked.length === 0 ? (
        <p className="text-sm text-faint mb-4" data-testid="usage-empty">
          {report.enabled ? 'Nothing in these days yet. It fills as you use the app.' : 'Recording is off.'}
        </p>
      ) : (
        <>
          <ul className="card divide-y divide-line overflow-hidden mb-2" data-testid="usage-ranking">
            {shown.map((r) => (
              <Row key={r.feature} r={r} />
            ))}
          </ul>
          {ranked.length > CAP && (
            <button type="button" className="text-xs text-muted mb-4 min-h-8" onClick={() => setAll(!all)}>
              {all ? 'Show fewer' : `Show all ${ranked.length}`}
            </button>
          )}
        </>
      )}

      <h3 className="text-[15px] font-semibold mt-4 mb-2">Not used for 30 days</h3>
      {wait > 0 ? (
        <p className="text-sm text-faint mb-4" data-testid="usage-flags-wait">
          {report.enabled && report.since
            ? `Flags start once 30 days are counted: ${wait} to go.`
            : 'Flags start once 30 days are counted.'}
        </p>
      ) : unused.length === 0 ? (
        <p className="text-sm text-faint mb-4">Everything was used in the last 30 days.</p>
      ) : (
        <>
          <ul className="card divide-y divide-line overflow-hidden mb-2" data-testid="usage-unused">
            {unusedShown.map((u) => (
              <li key={u.feature} className="flex items-baseline justify-between gap-2 px-3.5 py-2.5">
                <span className="text-[15px] truncate">{u.label}</span>
                <span className="text-xs text-faint shrink-0">
                  {u.lastDay ? `Last used ${fmtDateShort(u.lastDay)}` : 'Not used yet'}
                </span>
              </li>
            ))}
          </ul>
          {unused.length > CAP && (
            <button
              type="button"
              className="text-xs text-muted mb-4 min-h-8"
              onClick={() => setAllUnused(!allUnused)}
            >
              {allUnused ? 'Show fewer' : `Show all ${unused.length}`}
            </button>
          )}
        </>
      )}

      <div className={cx('mt-4')}>
        {confirming ? (
          <div className="flex items-center gap-2">
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => void clear()}
              data-testid="usage-clear-yes"
            >
              Clear all usage
            </Button>
            <Button onClick={() => setConfirming(false)}>Keep it</Button>
          </div>
        ) : (
          <Button onClick={() => setConfirming(true)} data-testid="usage-clear">
            Clear usage
          </Button>
        )}
        <p className="text-xs text-faint mt-1">
          Forgets every count and starts again from today. Counts older than 400 days are dropped by
          themselves.
        </p>
      </div>
    </div>
  )
}
